import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";
import {
  ArrowRight,
  CheckCircle,
  Star,
  Check,
  MapPin,
  Camera,
  Smartphone,
  ShoppingCart,
  Search,
  Bell,
} from "lucide-react";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

const AgentDashboardMockup = () => {
  return (
    <div className="relative">
      {/* Floating Background Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { Icon: MapPin, delay: 0, x: "10%", y: "15%" },
          { Icon: Star, delay: 0.5, x: "85%", y: "20%" },
          { Icon: ShoppingCart, delay: 1, x: "15%", y: "70%" },
          { Icon: CheckCircle, delay: 1.5, x: "80%", y: "75%" },
          { Icon: Camera, delay: 2, x: "50%", y: "10%" },
          { Icon: Smartphone, delay: 2.5, x: "90%", y: "50%" },
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.15, 0],
              scale: [0, 1, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: item.delay,
              ease: "easeInOut",
            }}
            className="absolute"
            style={{ left: item.x, top: item.y }}
          >
            <item.Icon className="w-16 h-16 text-blue-600" />
          </motion.div>
        ))}
      </div>

      {/* Main Dashboard Mockup */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden relative z-10"
      >
        <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
                My Sign Inventory & Orders
              </h3>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
              <Search className="w-4 h-4" />
              <Bell className="w-4 h-4" />
              <div className="w-7 h-7 rounded-full bg-gray-300"></div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                <CounterAnimation value={24} />
              </div>
              <p className="text-sm text-gray-600">Signs Installed</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-green-600">
                <CounterAnimation value={15} />
              </div>
              <p className="text-sm text-gray-600">In Storage</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold" style={{ color: "#f58b01" }}>
                <CounterAnimation value={3} />
              </div>
              <p className="text-sm text-gray-600">Damaged</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                <CounterAnimation value={5} />
              </div>
              <p className="text-sm text-gray-600">Pending Jobs</p>
            </Card>
          </div>

          <div className="space-y-3">
            {[
              {
                address: "1234 Beverly Hills Dr",
                status: "Installed",
                condition: "Good",
                location: "Front Yard",
              },
              {
                address: "5678 Manhattan Beach Ave",
                status: "Scheduled",
                condition: "New",
                location: "Curbside",
              },
              {
                address: "9101 Santa Monica Blvd",
                status: "In Storage",
                condition: "Good",
                location: "Warehouse",
              },
              {
                address: "1213 Culver City St",
                status: "Damaged",
                condition: "Replace",
                location: "Vendor",
              },
            ].map((listing, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{listing.address}</p>
                    <p className="text-sm text-gray-600">
                      {listing.condition} • {listing.location}
                    </p>
                  </div>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${listing.status === "Installed"
                    ? "bg-green-100 text-green-800"
                    : listing.status === "Scheduled"
                      ? "bg-blue-100 text-blue-800"
                      : listing.status === "In Storage"
                        ? "bg-gray-100 text-gray-800"
                        : "text-white"
                    }`}
                  style={listing.status === "Damaged" ? { backgroundColor: "#f58b01" } : {}}
                >
                  {listing.status}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function AgentsSolutionsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <AgentContent />
    </div>
  );
}

function AgentContent() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const agentChallenges: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "endlessCallsEmails",
      title: "Endless Calls & Emails",
      description: "Endless calls and emails just to schedule a sign install or removal.",
    },
    {
      icon: "noSignVisibility",
      title: "No Sign Visibility",
      description:
        "No clarity on sign condition or location — unsure if a sign is damaged, missing, or still in storage.",
    },
    {
      icon: "manualInventoryTracking",
      title: "Manual Inventory Tracking",
      description: "Manual inventory tracking — spreadsheets that never stay updated.",
    },
    {
      icon: "vendorConfusion",
      title: "Vendor Confusion",
      description:
        "Confusion with vendors — delayed installs, lost artwork, and inconsistent pricing.",
    },
    {
      icon: "noMobileVisibility",
      title: "No Mobile Visibility",
      description: "No mobile visibility — can't track orders or approvals on the go.",
    },
    {
      icon: "inconsistentCosts",
      title: "Inconsistent Costs",
      description:
        "No visibility into pricing or exclusive offers — paying full price even when preferred vendor discounts could apply.",
    },
  ];

  const coreFeatures: { icon: IconName; title: string; features: string[] }[] = [
    {
      icon: "smartOrdering",
      title: "Smart Sign Ordering",
      features: [
        "Order installations, removals, or replacements in seconds",
        "Choose from your brokerage's approved catalog",
        "See live pricing, taxes, and turnaround times — no calls or emails needed",
      ],
    },
    {
      icon: "realTimeJobTracking",
      title: "Real-Time Job Tracking",
      features: [
        "Track every job from request to completion",
        "Get photo proof, live status updates, and instant notifications",
      ],
    },
    {
      icon: "inventorySignCondition",
      title: "Inventory & Sign Condition",
      features: [
        "Know how many signs you own, where they are, and their condition",
        "Flag damaged signs, request replacements, and get restock alerts",
      ],
    },
    {
      icon: "flexibleHandover",
      title: "Flexible Handover Options",
      features: [
        "Choose how your signs are transferred — drop them at the vendor's warehouse or schedule a pickup from your location",
        "You can also arrange direct delivery to the installation site",
      ],
    },
    {
      icon: "mapBasedOrdering",
      title: "Map-Based Ordering",
      features: [
        "Pin the exact property location for installations",
        "Vendors get verified GPS coordinates for precise placement",
        "Reduce address errors and avoid installation confusion",
      ],
    },
    {
      icon: "fileSharingWorkdrive",
      title: "File Sharing with WorkDrive",
      features: [
        "Share listing photos, artwork, and sign designs securely",
        "Control access with passwords, expiry dates, and activity tracking",
      ],
    },
    {
      icon: "paymentDiscountOrdering",
      title: "Payments, Discounts & Order History",
      features: [
        "Pay securely online or through brokerage billing",
        "View invoices, taxes, and order history in one place",
        "Enjoy exclusive vendor discounts — like 5% off all sign orders from Farmer Signs",
      ],
    },
    {
      icon: "mobileAppForAgents",
      title: "Mobile App for Agents",
      features: [
        "Manage orders, payments, and inventory from your phone",
        "Approve jobs, upload artwork, and get real-time updates anywhere",
      ],
    },
  ];

  const outcomes: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "fullSignVisibility",
      title: "Full Sign Visibility",
      description: "Always know where each sign is and what condition it's in",
    },
    {
      icon: "fasterInstallation",
      title: "Faster Installations",
      description: "Schedule and confirm signs in hours, not days",
    },
    {
      icon: "lessAdminWork",
      title: "Less Admin Work",
      description: "No emails or back-and-forth with vendors",
    },
    {
      icon: "consistencyBranding",
      title: "Consistent Branding",
      description: "Use brokerage-approved templates and artwork",
    },
    {
      icon: "accurateTracking",
      title: "Accurate Location Tracking",
      description: "GPS-based ordering reduces installation errors",
    },
    {
      icon: "financialClarity",
      title: "Financial Clarity",
      description: "See order costs, invoices, and payments at a glance",
    },
    {
      icon: "onTheGoManagement",
      title: "On-the-Go Management",
      description: "Everything accessible through the mobile app",
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: MapPin, delay: 0, x: "10%", y: "15%" },
            { Icon: Star, delay: 0.5, x: "85%", y: "20%" },
            { Icon: ShoppingCart, delay: 1, x: "15%", y: "70%" },
            { Icon: CheckCircle, delay: 1.5, x: "80%", y: "75%" },
            { Icon: Camera, delay: 2, x: "50%", y: "10%" },
            { Icon: Smartphone, delay: 2.5, x: "90%", y: "50%" },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.15, 0],
                scale: [0, 1, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut",
              }}
              className="absolute"
              style={{ left: item.x, top: item.y }}
            >
              <item.Icon className="w-16 h-16 text-blue-600" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    Your Signage. Streamlined.
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    RealEsy is the real estate sign installation app that lets agents purchase,
                    order, and track signage with complete transparency. From new sign purchases to
                    installations and removals, every job stays organized, every update visible, and
                    every listing looks consistent.
                  </p>
                  <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3 justify-center flex-wrap">
                      <Badge className="bg-green-600 text-white px-3 py-1 text-sm font-bold">
                        Exclusive Partner Offer
                      </Badge>
                      <p className="text-base md:text-lg text-gray-800">
                        Get <span className="font-bold text-green-600">5% off</span> all sign orders
                        from Farmer Signs when you purchase through RealEsy.
                      </p>
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                    (More vendor options coming soon, along with the ability to invite your
                    preferred partners to RealEsy.)
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                    >
                      Get Started
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <AgentDashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Agent Challenges Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Agent Challenges (Before RealEsy)
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                The daily frustrations agents face when managing signage without a unified platform.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agentChallenges.map((challenge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="p-6 rounded-2xl border-2 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-red-500 rounded-xl shadow-md border border-gray-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={challenge.icon} size={26} alt={challenge.title} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{challenge.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{challenge.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Core Features of RealEsy for Agents
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything you need to manage your real estate signage efficiently and
                professionally.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Outcomes & Benefits Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Outcomes & Benefits
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Transform your daily workflow and deliver exceptional service to your clients.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {outcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={outcome.icon} size={30} alt={outcome.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{outcome.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{outcome.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Coming Soon Section */}
      <FeatureSection className="bg-white relative overflow-hidden">
        <div className="container mx-auto px-6 relative">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <div className="inline-block mb-6">
                <Badge className="bg-white text-dark border-2 border-gray-200 px-4 py-2 text-base inline-flex items-center gap-2">
                  <AppIcon name="comingSoon" size={32} alt="Coming Soon" />
                  Coming Soon
                </Badge>
              </div>

              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                More Vendors. More Power.
              </h2>

              <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
                RealEsy is built to grow with you. While it currently streamlines every part of your
                signage workflow, the platform is designed to expand — connecting agents with more
                vendor partners and operational tools that make real-estate management even easier.
              </p>
            </motion.div>
          </div>
        </div>
      </FeatureSection>

      {/* Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              className="absolute w-8 h-8"
            >
              {i % 3 === 0 ? (
                <MapPin className="w-full h-full text-white" />
              ) : i % 3 === 1 ? (
                <Star className="w-full h-full text-white" />
              ) : (
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Focus on Selling — We'll Handle the Signs.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              RealEsy is the real estate sign installation and management app that helps agents
              order, track, and manage signage with confidence — and soon, even more vendor
              services.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white w-full sm:w-auto"
                >
                  Get Started
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
